import { createContext, useCallback, useContext, useMemo, useState } from "react"

const STORAGE_KEY = "ff-overrides"

/**
 * @typedef {Object} FeatureFlagOverrideContextValue
 * @property {Object<string, boolean>} overrides
 * @property {(flagKey: string, value: boolean) => void} setOverride
 * @property {() => void} clearAllOverrides
 */

/** @type {React.Context<FeatureFlagOverrideContextValue|null>} */
export const FeatureFlagOverrideContext = createContext(null)

/**
 * Hook to access feature flag override context.
 * Must be used within a FeatureFlagOverrideProvider.
 *
 * @returns {FeatureFlagOverrideContextValue} Feature flag override context value
 * @throws {Error} When used outside of FeatureFlagOverrideProvider
 */
export function useFeatureFlagOverrides() {
  const context = useContext(FeatureFlagOverrideContext)

  if (context === null) {
    throw new Error(
      "useFeatureFlagOverrides must be used within a FeatureFlagOverrideProvider"
    )
  }

  return context
}

/**
 * Reads feature flag overrides from localStorage.
 * Returns an empty object if the stored value is missing, corrupt, or not a plain object.
 *
 * @returns {Object<string, boolean>} Stored overrides keyed by flag key
 */
function readFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) {
      return {}
    }

    const parsed = JSON.parse(raw)
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed
    }

    return {}
  } catch {
    return {}
  }
}

/**
 * Persists feature flag overrides to localStorage.
 *
 * @param {Object<string, boolean>} overrides - Overrides keyed by flag key
 */
function writeToStorage(overrides) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(overrides))
}

/**
 * Provider component that manages feature flag override state for development.
 * Persists overrides to localStorage so they survive page reloads.
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @returns {React.ReactElement} Provider component
 */
export function FeatureFlagOverrideProvider({ children }) {
  const [overrides, setOverrides] = useState(readFromStorage)

  const setOverride = useCallback((flagKey, value) => {
    setOverrides((previous) => {
      const next = { ...previous, [flagKey]: value }
      writeToStorage(next)
      return next
    })
  }, [])

  const clearAllOverrides = useCallback(() => {
    setOverrides({})
    writeToStorage({})
  }, [])

  const value = useMemo(
    () => ({
      overrides,
      setOverride,
      clearAllOverrides,
    }),
    [overrides, setOverride, clearAllOverrides]
  )

  return (
    <FeatureFlagOverrideContext.Provider value={value}>
      {children}
    </FeatureFlagOverrideContext.Provider>
  )
}
